import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MapPin, RotateCcw, Save, Power } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import SuperAdminOnlyNote from "../components/SuperAdminOnlyNote";
import api from "../services/api";
import { isSuperAdmin } from "../utils/role";

// Where Zooma actually operates.
//
// These polygons replaced two hardcoded lat/lng rectangles in the backend, so
// expanding to a new campus is now a settings change rather than a release. They
// also decide which riders receive orders at all — a rider outside every enabled
// area gets nothing, which used to be an invisible constant and is now something
// an admin can see and fix.
//
// The polygon editor here is a validated coordinate list, not a draw-on-map tool.
// That's a deliberate scope choice: a map editor is a real build, and the areas
// change rarely. Coordinates can be lifted straight from Google Maps.

const emptyArea = () => ({ name: "", polygon: [], enabled: true });

const formatPolygon = (polygon) =>
  Array.isArray(polygon)
    ? polygon.map((pair) => `${pair[0]}, ${pair[1]}`).join("\n")
    : "";

/**
 * Parse "lng, lat" per line. Returns { polygon, error } so the caller can show a
 * precise complaint rather than silently dropping a bad row — a malformed
 * polygon would otherwise quietly shrink the area riders can be matched in.
 */
const parsePolygon = (text) => {
  const lines = String(text)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const polygon = [];
  for (let index = 0; index < lines.length; index += 1) {
    const parts = lines[index].split(",").map((part) => part.trim());
    if (parts.length !== 2) {
      return { error: `Line ${index + 1}: expected "lng, lat"` };
    }
    const lng = Number(parts[0]);
    const lat = Number(parts[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return { error: `Line ${index + 1}: not a number` };
    }
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) {
      return {
        error: `Line ${index + 1}: out of range — remember it's lng first, then lat`,
      };
    }
    polygon.push([lng, lat]);
  }

  if (polygon.length > 0 && polygon.length < 3) {
    return { error: "A polygon needs at least 3 points" };
  }
  return { polygon };
};

const ServiceAreas = () => {
  const [geofenceEnabled, setGeofenceEnabled] = useState(true);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canEdit = isSuperAdmin();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/v1/admin/service-areas");
      const data = response.data || {};
      setGeofenceEnabled(data.geofenceEnabled !== false);
      setAreas(
        (data.areas || []).map((area) => ({
          name: area.name || "",
          polygon: area.polygon || [],
          enabled: area.enabled !== false,
          polygonText: formatPolygon(area.polygon),
        })),
      );
    } catch (error) {
      console.error("Failed to load service areas:", error);
      toast.error(
        error?.response?.data?.message || "Could not load service areas",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateArea = (index, patch) => {
    setAreas((prev) =>
      prev.map((area, i) => (i === index ? { ...area, ...patch } : area)),
    );
  };

  const save = async () => {
    if (!canEdit) return;
    const parsed = [];
    for (let index = 0; index < areas.length; index += 1) {
      const area = areas[index];
      if (!area.name.trim()) {
        toast.error(`Area ${index + 1} needs a name`);
        return;
      }
      const result = parsePolygon(area.polygonText ?? "");
      if (result.error) {
        toast.error(`${area.name || `Area ${index + 1}`}: ${result.error}`);
        return;
      }
      if (result.polygon.length < 3) {
        toast.error(`${area.name}: a polygon needs at least 3 points`);
        return;
      }
      parsed.push({
        name: area.name.trim(),
        polygon: result.polygon,
        enabled: area.enabled,
      });
    }

    const enabledCount = parsed.filter((area) => area.enabled).length;
    if (geofenceEnabled && enabledCount === 0) {
      // Worth being explicit: the backend falls OPEN in this case rather than
      // excluding every rider, so this is a warning, not a block.
      const proceed = window.confirm(
        "The geofence is on but no area is enabled. Matching will fall back to " +
          "allowing riders anywhere. Save anyway?",
      );
      if (!proceed) return;
    }

    setSaving(true);
    try {
      await api.put("/v1/admin/service-areas", {
        geofenceEnabled,
        areas: parsed,
      });
      toast.success("Service areas saved");
      load();
    } catch (error) {
      console.error("Failed to save service areas:", error);
      toast.error(
        error?.response?.data?.message || "Could not save service areas",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Service areas
              </h1>
              <p className="max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                Riders outside every enabled area receive no orders. Coordinates
                are <strong>longitude first, then latitude</strong> — one point
                per line, copied straight from Google Maps.
              </p>
              {!canEdit && <SuperAdminOnlyNote className="mt-3" />}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                className="rounded-lg border border-gray-300 p-2 text-gray-600 dark:border-gray-600 dark:text-gray-300"
                title="Reload"
              >
                <RotateCcw size={16} />
              </button>
              {canEdit && (
                <button
                  onClick={save}
                  disabled={saving || loading}
                  className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white disabled:opacity-50 dark:bg-white dark:text-gray-900"
                >
                  <Save size={16} /> {saving ? "Saving…" : "Save changes"}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-start gap-3">
                  <Power
                    size={18}
                    className={
                      geofenceEnabled ? "text-green-600" : "text-gray-400"
                    }
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Geofence {geofenceEnabled ? "on" : "off"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {geofenceEnabled
                        ? "Only riders inside an enabled area are matched."
                        : "Riders are matched regardless of where they are."}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={() => setGeofenceEnabled((value) => !value)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200"
                  >
                    Turn {geofenceEnabled ? "off" : "on"}
                  </button>
                )}
              </div>

              {areas.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                  No areas configured. With the geofence on and no areas,
                  matching deliberately falls back to allowing riders anywhere
                  rather than excluding everyone.
                </div>
              )}

              {areas.map((area, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-gray-400" />
                      <input
                        value={area.name}
                        onChange={(event) =>
                          updateArea(index, { name: event.target.value })
                        }
                        placeholder="Area name"
                        readOnly={!canEdit}
                        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={area.enabled}
                          onChange={(event) =>
                            updateArea(index, { enabled: event.target.checked })
                          }
                          disabled={!canEdit}
                        />
                        Enabled
                      </label>
                      {canEdit && (
                        <button
                          onClick={() =>
                            setAreas((prev) => prev.filter((_, i) => i !== index))
                          }
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    value={area.polygonText ?? ""}
                    onChange={(event) =>
                      updateArea(index, { polygonText: event.target.value })
                    }
                    rows={Math.max(4, (area.polygonText ?? "").split("\n").length)}
                    placeholder={"4.65, 8.46\n4.69, 8.46\n4.69, 8.50\n4.65, 8.50"}
                    readOnly={!canEdit}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {(area.polygonText ?? "").split("\n").filter(Boolean).length}{" "}
                    point(s) · at least 3 required · the shape closes itself
                  </p>
                </div>
              ))}

              {canEdit && (
                <button
                  onClick={() =>
                    setAreas((prev) => [...prev, { ...emptyArea(), polygonText: "" }])
                  }
                  className="rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300"
                >
                  + Add area
                </button>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ServiceAreas;
